// apps/merchant-v3/src/lib/auth/useMe.ts
//
// Phase 6 Wave 1 Task 03 — TanStack Query hook for `GET /api/v1/auth/me`.
// Canonical bootstrap source for the current merchant user — consumed by
// every page header (TopBar tier badge, sidebar suspend banner, billing
// branch render). Cache policy: `staleTime` 30s; the hook
// is the single source of truth so pages should call `useMe()` instead
// of reading the Zustand store directly.
//
// Side-effect: when the server returns a fresh `User`, the result is
// mirrored into the Zustand `useUserStore` so non-React-Query
// consumers (e.g. selectors in `useCanMutate`) stay in sync.
//
// vg-binding: PLAN:task-03
// vg-binding: P6.D-02
// vg-binding: P6.D-205
// vg-binding: INTERFACE-STANDARDS:response-envelope

import { useEffect } from 'react';
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient, ApiError } from '@/lib/api/client';
import { useUserStore } from '@/store/user';
import type { MeResponse, MeUser } from '@/types/auth';
import { mapMeToUser } from '@/types/auth';
import type { BillingMode } from '@/types/user';

/** Stable React Query cache key for the current user. */
export const ME_QUERY_KEY = ['auth', 'me'] as const;

type AuthMeWire = Partial<MeUser> & {
  displayName?: string;
  primaryRole?: string;
  activeRoleSlot?: string;
  merchantId?: string | null;
  vendorId?: string | null;
  themePreference?: string;
  emailVerifiedAt?: string | null;
  actualUser?: {
    id?: string;
    displayName?: string;
    startedAt?: string;
  } | null;
  isImpersonating?: boolean;
  billing_mode?: string;
  billing?: {
    billingMode?: string;
    billing_mode?: string;
    mode?: string;
  };
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function nonEmptyString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function normalizeBillingMode(value: unknown): BillingMode {
  const mode = String(value ?? '').toUpperCase();
  return mode === 'PREPAID' || mode === 'INVOICE' || mode === 'CREDIT'
    ? mode
    : 'PREPAID';
}

function normalizeLanguage(value: unknown): MeUser['language'] {
  const language = String(value ?? '').toLowerCase();
  return language === 'en' || language === 'vi' || language === 'fr'
    ? language
    : 'en';
}

function normalizeTheme(value: unknown): MeUser['theme'] {
  return value === 'light' || value === 'dark' || value === 'system'
    ? value
    : 'system';
}

function normalizeCurrency(value: unknown): MeUser['displayCurrency'] {
  return value === 'USD' ||
    value === 'EUR' ||
    value === 'AUD' ||
    value === 'CAD' ||
    value === 'GBP'
    ? value
    : 'USD';
}

function normalizeRole(raw: AuthMeWire): MeUser['role'] {
  const role = String(raw.role ?? raw.primaryRole ?? raw.activeRoleSlot ?? '');
  return role === 'team_member' || role.includes('team')
    ? 'team_member'
    : 'merchant';
}

function normalizeStatus(value: unknown): MeUser['status'] {
  const status = String(value ?? '').toLowerCase();
  if (status === 'suspended' || status === 'banned') return 'suspended';
  if (
    status === 'deactivated' ||
    status === 'deleted' ||
    status === 'inactive'
  ) {
    return 'deactivated';
  }
  return 'active';
}

function normalizeImpersonation(raw: AuthMeWire): MeUser['impersonatedBy'] {
  if (raw.impersonatedBy) return raw.impersonatedBy;
  if (!raw.isImpersonating || !raw.actualUser) return null;
  return {
    adminId: nonEmptyString(raw.actualUser.id),
    adminName: nonEmptyString(raw.actualUser.displayName, raw.actualUser.id),
    startedAt: nonEmptyString(raw.actualUser.startedAt),
  };
}

function normalizeMe(rawValue: unknown): MeUser {
  const raw = asRecord(rawValue) as AuthMeWire;
  const billing = asRecord(raw.billing);
  const email = nonEmptyString(raw.email);
  const name = nonEmptyString(
    raw.name,
    nonEmptyString(raw.displayName, email.split('@')[0] ?? 'Merchant'),
  );

  return {
    id: nonEmptyString(raw.id, nonEmptyString(raw.merchantId)),
    email,
    name,
    avatarUrl: raw.avatarUrl ?? null,
    phone: raw.phone ?? null,
    role: normalizeRole(raw),
    parentMerchantId: raw.parentMerchantId ?? raw.merchantId ?? null,
    // dbg-20260929-loyaltytier — NO fallback.
    //
    // This read `raw.loyaltyTier ?? 'Titanium'` while GET /auth/me did not
    // send the field at all, so every merchant in the portal wore a Titanium
    // badge — including Diamond merchants looking at correctly-resolved
    // Diamond prices. The server now sends it; a missing value means the
    // merchant has no tier, which the UI shows by omitting the badge.
    loyaltyTier: raw.loyaltyTier ?? null,
    billingMode: normalizeBillingMode(
      raw.billingMode ??
        raw.billing_mode ??
        billing.billingMode ??
        billing.billing_mode ??
        billing.mode,
    ),
    status: normalizeStatus(raw.status),
    suspendReason: raw.suspendReason ?? null,
    suspendContact: raw.suspendContact ?? null,
    language: normalizeLanguage(raw.language),
    timezone: nonEmptyString(raw.timezone, 'UTC'),
    displayCurrency: normalizeCurrency(raw.displayCurrency),
    theme: normalizeTheme(raw.theme ?? raw.themePreference),
    // R9-4 (2026-05-17, BUG-P6-DEBUG-R2-B7) — surface persisted density
    // so PreferencesPanel rehydrates. BE returns it on GET /auth/me now.
    density: (() => {
      const d = (raw as { density?: unknown }).density;
      return d === 'compact' || d === 'normal' ? d : 'normal';
    })(),
    twoFactorEnabled: Boolean(raw.twoFactorEnabled),
    emailVerified:
      typeof raw.emailVerified === 'boolean'
        ? raw.emailVerified
        : Boolean(raw.emailVerifiedAt),
    permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
    impersonatedBy: normalizeImpersonation(raw),
    // R8-1 (2026-05-17, BUG-P6-DEBUG-B3): hydrate persisted billing info
    // so /settings/general can pre-fill the BillingInfoForm. BE sends
    // `billingInfo`; the prior normalizer only read `billing` (mode-only
    // legacy alias) and dropped the rich object, so the form rendered
    // empty after every reload even when the BE had the data.
    billingInfo: normalizeBillingInfo(raw.billingInfo),
  };
}

/** Project the BE billingInfo block onto the FE BillingInfo shape.
 *  Returns null when any required field is missing so the form falls
 *  back to its EMPTY_BILLING defaults (no half-populated UI). */
function normalizeBillingInfo(
  raw: unknown,
): MeUser['billingInfo'] {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const legalName = typeof r.legalName === 'string' ? r.legalName : null;
  const addressLine1 =
    typeof r.addressLine1 === 'string' ? r.addressLine1 : null;
  const city = typeof r.city === 'string' ? r.city : null;
  const country = typeof r.country === 'string' ? r.country : null;
  if (!legalName || !addressLine1 || !city || !country) return null;
  return {
    legalName,
    taxId: typeof r.taxId === 'string' ? r.taxId : undefined,
    addressLine1,
    addressLine2:
      typeof r.addressLine2 === 'string' ? r.addressLine2 : undefined,
    city,
    state: typeof r.state === 'string' ? r.state : undefined,
    postalCode:
      typeof r.postalCode === 'string' ? r.postalCode : undefined,
    country,
  };
}

/** Fetch the current user via `GET /api/v1/auth/me`. */
async function fetchMe(): Promise<MeUser> {
  // The api/client already unwraps the success envelope; on 401 the
  // shared interceptor triggers /auth/refresh and replays. If refresh
  // fails the call rejects with `ApiError { code: 'UNAUTHENTICATED' }`
  // which RequireAuth catches to redirect.
  const raw = await apiClient.get<MeResponse['data'] | unknown>(
    '/api/v1/auth/me',
  );
  return normalizeMe(raw);
}

// Debug B1 — known public routes where /auth/me is guaranteed to 401
// on first load (no session yet). Skipping the fetch on these routes
// stops the browser-native "Failed to load resource: 401" console
// noise without changing the auth contract.
//
// dbg-20260704 (ACC-10) — `/login` is deliberately NOT in this set. An
// already-authenticated user who hard-loads /login (typed URL / browser
// back) has a valid cookie but no cached ME; skipping the fetch left
// `user` undefined so the LoginPage authed-redirect guard never fired and
// the login form rendered instead of bouncing to /dashboard. Fetching
// /auth/me on /login recognises the session (redirect) and, for a genuinely
// logged-out visitor, costs one harmless 401 — the AuthProvider 401 handler
// treats /login as public and does NOT redirect-loop.
const PUBLIC_AUTH_ROUTES = new Set([
  '/forgot-password',
  '/reset-password',
  '/register',
  '/verify-email',
]);

function isPublicAuthRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return PUBLIC_AUTH_ROUTES.has(window.location.pathname);
}

/** Main consumer hook — used by AuthProvider bootstrap + pages. */
export function useMe(): UseQueryResult<MeUser, ApiError> {
  const setUser = useUserStore((s) => s.setUser);

  const result = useQuery<MeUser, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: (failureCount, err) => {
      // Don't retry auth failures — the api/client already handled
      // the 401 refresh round-trip. Surface them to RequireAuth.
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: true,
    enabled: !isPublicAuthRoute(),
  });

  // Mirror server-truth into Zustand for non-RQ consumers (canMutate,
  // SuspendBanner, TopBar etc.). Empty cache → clear store.
  useEffect(() => {
    if (result.data) {
      setUser(mapMeToUser(result.data));
    } else if (result.isError && result.error?.status === 401) {
      setUser(null);
    }
  }, [result.data, result.isError, result.error, setUser]);

  return result;
}

/** Imperative invalidation — used by login/logout to force refetch. */
export function useInvalidateMe(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
  };
}
